import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Alert, TextField, MenuItem, Stack, Button,
  Snackbar, Pagination,
} from '@mui/material';
import { Wallet } from 'lucide-react';
import { fetchPayouts, processPayout } from '../utils/partnerApi';
import PageHeader from '../components/common/PageHeader';
import EmptyState from '../components/common/EmptyState';
import { TableLoadingSkeleton } from '../components/common/LoadingState';
import StatusBadge from '../components/common/StatusBadge';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from '../components/common/modal';
import ExportTableButton from '../components/common/ExportTableButton';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

const PartnerPayoutsView = () => {
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('requested');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [razorpayxEnabled, setRazorpayxEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [payDialog, setPayDialog] = useState({ open: false, payout: null });
  const [utr, setUtr] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: 25 };
      if (statusFilter) params.status = statusFilter;
      const res = await fetchPayouts(params);
      setPayouts(res.data.payouts || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
      setRazorpayxEnabled(!!res.data.razorpayx_enabled);
    } catch (e) {
      setError('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, action, extra = {}) => {
    setBusy(true);
    try {
      await processPayout(id, { action, ...extra });
      const verb = { paid: 'marked paid', disburse: 'sent to RazorpayX', reconcile: 'reconciled', approve: 'approved', reject: 'rejected' }[action] || `${action}d`;
      notify(`Payout ${verb}`);
      setPayDialog({ open: false, payout: null });
      setUtr('');
      load();
    } catch (e) {
      notify(e.response?.data?.msg || 'Action failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Partner Payouts"
        subtitle="Review, approve, and disburse partner commissions and withdrawal requests."
        count={total}
        countLabel="payouts"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Growth', href: '/partners' },
          { label: 'Payouts' }
        ]}
        secondaryAction={
          <ExportTableButton
            data={payouts}
            total={total}
            onExportAll={async () => {
              const params = { all: true };
              if (statusFilter) params.status = statusFilter;
              const res = await fetchPayouts(params);
              return res.data?.payouts || [];
            }}
            columns={[
              { label: 'Partner Name', value: (r) => r.partner?.full_name || r.partner_id?.full_name || 'N/A' },
              { label: 'Partner Phone', value: (r) => r.partner?.phone || r.partner_id?.phone || 'N/A' },
              { label: 'Partner Code', value: (r) => r.partner?.partnerCode || 'N/A' },
              { label: 'Amount (₹)', key: 'amount' },
              { label: 'Method', value: (r) => r.method || r.payout_method || 'N/A' },
              { label: 'Destination', value: (r) => r.destination || r.upi_id || r.bank_account?.account_number || 'N/A' },
              { label: 'Status', key: 'status' },
              { label: 'UTR Reference', key: 'utr_reference' },
              { label: 'Requested At', value: (r) => r.requested_at || r.createdAt ? new Date(r.requested_at || r.createdAt).toLocaleString() : 'N/A' },
            ]}
            filename="partner_payouts"
          />
        }
      />

      <Card sx={{ p: 2, borderRadius: 2.5, mb: 2 }}>
        <TextField size="small" select label="Status" value={statusFilter}
          onChange={(e) => { setPage(1); setStatusFilter(e.target.value); }} sx={{ minWidth: { xs: '100%', sm: 200 } }}>
          <MenuItem value="">All Statuses</MenuItem>
          <MenuItem value="requested">Requested</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="processing">Processing</MenuItem>
          <MenuItem value="paid">Paid</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
        </TextField>
      </Card>

      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {['Partner', 'Amount', 'Method', 'Destination', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h} align={h === 'Actions' ? 'right' : 'left'}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableLoadingSkeleton rows={10} cols={6} />
              ) : payouts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={Wallet}
                      title="No payout requests found"
                      description={statusFilter ? `No withdrawal requests currently with status "${statusFilter}".` : "No partner payouts have been requested yet."}
                    />
                  </TableCell>
                </TableRow>
              ) : payouts.map((p) => (
                    <TableRow key={p._id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700 }}>{p.partner?.full_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.partner?.phone} · {p.partner?.partnerCode}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>{inr(p.amount)}</TableCell>
                      <TableCell sx={{ textTransform: 'uppercase', fontSize: '0.78rem' }}>{p.method}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>
                        {p.method === 'upi'
                          ? p.destination?.upi_id
                          : `${p.destination?.account_number || ''} · ${p.destination?.ifsc || ''}`}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={p.status} />
                      </TableCell>
                      <TableCell>
                        {['requested', 'approved'].includes(p.status) ? (
                          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                            {p.status === 'requested' && (
                              <Button size="small" variant="outlined" disabled={busy} onClick={() => act(p._id, 'approve')} sx={{ textTransform: 'none' }}>Approve</Button>
                            )}
                            {razorpayxEnabled && (
                              <Button size="small" variant="contained" color="primary" disabled={busy} onClick={() => act(p._id, 'disburse')} sx={{ textTransform: 'none' }}>Disburse (RazorpayX)</Button>
                            )}
                            <Button size="small" variant="outlined" color="success" disabled={busy} onClick={() => { setPayDialog({ open: true, payout: p }); setUtr(''); }} sx={{ textTransform: 'none' }}>Mark Paid</Button>
                            <Button size="small" variant="outlined" color="error" disabled={busy} onClick={() => act(p._id, 'reject')} sx={{ textTransform: 'none' }}>Reject</Button>
                          </Stack>
                        ) : p.status === 'processing' ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="caption" color="text.secondary">Auto-paying…</Typography>
                            <Button size="small" variant="outlined" disabled={busy} onClick={() => act(p._id, 'reconcile')} sx={{ textTransform: 'none' }}>Reconcile</Button>
                          </Stack>
                        ) : p.status === 'failed' ? (
                          <Typography variant="caption" color="error.main">{p.failure_reason || 'Failed — wallet refunded'}</Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">{p.utr_reference || '—'}</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, borderTop: 1, borderColor: 'divider' }}>
            <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" />
          </Box>
        )}
      </Card>

      {/* Mark Paid Dialog */}
      <AdminDialog
        open={payDialog.open}
        onClose={() => setPayDialog({ open: false, payout: null })}
        maxWidth="sm"
      >
        <AdminDialogHeader
          title="Mark Payout as Paid"
          subtitle="Record bank/UPI reference for partner transfer"
          icon={Wallet}
          onClose={() => setPayDialog({ open: false, payout: null })}
        />
        <AdminDialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirm you have transferred <strong>{inr(payDialog.payout?.amount)}</strong> to the partner, then record the bank/UPI reference.
          </Typography>
          <TextField
            fullWidth
            label="UTR / Reference number"
            value={utr}
            onChange={(e) => setUtr(e.target.value)}
            placeholder="e.g. UPI/123456789012 or IMPS ref"
          />
        </AdminDialogContent>
        <AdminDialogActions>
          <Button onClick={() => setPayDialog({ open: false, payout: null })} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={busy}
            onClick={() => act(payDialog.payout._id, 'paid', { utr_reference: utr })}
            sx={{ px: 3, fontWeight: 700 }}
          >
            Confirm Paid
          </Button>
        </AdminDialogActions>
      </AdminDialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ fontWeight: 600 }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PartnerPayoutsView;
